import {computed, ref} from 'vue';
import axios from 'axios';
import type {RouteLocationNormalizedLoaded} from 'vue-router';
import {
    type FeatureFlow,
    featureFlows,
    type FeatureFlowStep,
    type FeatureHint,
    featureHints,
    type HintAudience,
} from '@/config/featureHints';
import {d} from '@/utils/consoleHelper.ts';

export interface HintsSessionPayload {
    dismissed?: string[];
    userCreatedAt?: string | null;
}

export interface ActiveFlowStep {
    flow: FeatureFlow;
    step: FeatureFlowStep;
    stepIndex: number;
    totalSteps: number;
    isLastStep: boolean;
}

export type ActiveSpotlight =
    | { kind: 'hint'; hint: FeatureHint }
    | ({ kind: 'flow-step' } & ActiveFlowStep);

const FLOW_STEP_STORAGE_PREFIX = 'fw-hint-flow:';

const dismissedKeys = ref<Set<string>>(new Set());
const seenThisSession = ref<Set<string>>(new Set());
const userCreatedAt = ref<string | null>(null);
const hydrated = ref(false);
const flowStepIndexes = ref<Record<string, number>>({});

/**
 * Called from the session fetch (auth store) — hints are only shown once
 * the server-side dismissal state is known.
 */
export function hydrateFeatureHints(payload: HintsSessionPayload | null | undefined): void {
    dismissedKeys.value = new Set(payload?.dismissed ?? []);
    seenThisSession.value = new Set();
    userCreatedAt.value = payload?.userCreatedAt ?? null;
    flowStepIndexes.value = {};
    hydrated.value = true;
}

function matchesAudience(audience: HintAudience, shippedAt: string): boolean {
    if (audience === 'all') {
        return true;
    }
    if (!userCreatedAt.value) {
        return audience === 'existing-users';
    }
    const createdBeforeShipped = new Date(userCreatedAt.value) < new Date(shippedAt);
    return audience === 'existing-users' ? createdBeforeShipped : !createdBeforeShipped;
}

function withinWindow(shippedAt: string, expiresAt: string, now: Date): boolean {
    return now >= new Date(shippedAt) && now <= new Date(expiresAt);
}

export function isHintEligible(hint: FeatureHint, now: Date = new Date()): boolean {
    return !dismissedKeys.value.has(hint.key)
        && withinWindow(hint.shippedAt, hint.expiresAt, now)
        && matchesAudience(hint.audience ?? 'existing-users', hint.shippedAt);
}

export function isFlowEligible(flow: FeatureFlow, now: Date = new Date()): boolean {
    return !dismissedKeys.value.has(flow.key)
        && flow.steps.length > 0
        && withinWindow(flow.shippedAt, flow.expiresAt, now)
        && matchesAudience(flow.audience, flow.shippedAt);
}

function readStoredFlowStep(flowKey: string): number {
    try {
        const raw = window.localStorage.getItem(FLOW_STEP_STORAGE_PREFIX + flowKey);
        const parsed = raw === null ? 0 : Number.parseInt(raw, 10);
        return Number.isNaN(parsed) ? 0 : parsed;
    } catch {
        return 0;
    }
}

function currentFlowStepIndex(flow: FeatureFlow): number {
    if (!(flow.key in flowStepIndexes.value)) {
        flowStepIndexes.value[flow.key] = readStoredFlowStep(flow.key);
    }
    return Math.min(flowStepIndexes.value[flow.key] ?? 0, flow.steps.length - 1);
}

function setFlowStepIndex(flow: FeatureFlow, index: number): void {
    flowStepIndexes.value[flow.key] = index;
    try {
        window.localStorage.setItem(FLOW_STEP_STORAGE_PREFIX + flow.key, String(index));
    } catch {
        // Storage unavailable — progress just won't survive a refresh.
    }
}

function clearFlowStepIndex(flow: FeatureFlow): void {
    delete flowStepIndexes.value[flow.key];
    try {
        window.localStorage.removeItem(FLOW_STEP_STORAGE_PREFIX + flow.key);
    } catch {
        // Storage unavailable — nothing to clear.
    }
}

/**
 * Wipe all locally-stored flow progress so multi-step tours restart from step
 * one. Dev/testing helper: flow progress persists in localStorage independently
 * of the server-side dismissed state, so clearing user_hints rows alone won't
 * restart a partially-completed tour. Server-dismissed hints still need their
 * user_hints row removed to reappear.
 */
export function resetLocalFlowProgress(): void {
    flowStepIndexes.value = {};
    try {
        Object.keys(window.localStorage)
            .filter((key) => key.startsWith(FLOW_STEP_STORAGE_PREFIX))
            .forEach((key) => window.localStorage.removeItem(key));
    } catch {
        // Storage unavailable — nothing to clear.
    }
}

function postHintEvent(key: string, event: 'seen' | 'dismiss'): void {
    axios.post(`/v1/hints/${key}/${event}`).catch((error) => d.error(`Failed to mark hint ${event}:`, error));
}

function buildActiveFlowStep(flow: FeatureFlow, stepIndex: number): ActiveFlowStep {
    return {
        flow,
        step: flow.steps[stepIndex] as FeatureFlowStep,
        stepIndex,
        totalSteps: flow.steps.length,
        isLastStep: stepIndex === flow.steps.length - 1,
    };
}

export function useFeatureHints() {
    /**
     * The flow step to display for the current route, if any. If the route
     * matches a LATER step than the stored one, progress jumps forward — this
     * is what advances a 'target' step whose click navigated to the next page.
     */
    const flowStepForRoute = (route: RouteLocationNormalizedLoaded): ActiveFlowStep | null => {
        const flow = featureFlows.find((candidate) => isFlowEligible(candidate));
        if (!flow) {
            return null;
        }

        const storedIndex = currentFlowStepIndex(flow);
        const matchedIndex = flow.steps.findIndex(
            (step, index) => index >= storedIndex && step.routeName === route.name,
        );

        if (matchedIndex === -1) {
            return null;
        }

        if (matchedIndex > storedIndex) {
            setFlowStepIndex(flow, matchedIndex);
        }

        return buildActiveFlowStep(flow, matchedIndex);
    };

    const hintForRoute = (route: RouteLocationNormalizedLoaded): FeatureHint | null => {
        return featureHints.find((hint) => hint.routeName === route.name && isHintEligible(hint)) ?? null;
    };

    /** Flows take priority over single hints — never show two popovers at once. */
    const spotlightForRoute = (route: RouteLocationNormalizedLoaded): ActiveSpotlight | null => {
        if (!hydrated.value) {
            return null;
        }

        const flowStep = flowStepForRoute(route);
        if (flowStep) {
            return {kind: 'flow-step', ...flowStep};
        }

        const hint = hintForRoute(route);
        return hint ? {kind: 'hint', hint} : null;
    };

    const markSeen = (key: string): void => {
        if (seenThisSession.value.has(key)) {
            return;
        }
        seenThisSession.value.add(key);
        postHintEvent(key, 'seen');
    };

    const dismiss = (key: string): void => {
        dismissedKeys.value.add(key);
        postHintEvent(key, 'dismiss');
    };

    /** Advance a flow one step. On the last step, use dismissFlow instead. */
    const advanceFlow = (flow: FeatureFlow): void => {
        const next = currentFlowStepIndex(flow) + 1;
        if (next >= flow.steps.length) {
            dismissFlow(flow);
            return;
        }
        setFlowStepIndex(flow, next);
    };

    /** Dismiss the whole flow (final "Got it" or "Skip tour"). */
    const dismissFlow = (flow: FeatureFlow): void => {
        clearFlowStepIndex(flow);
        dismiss(flow.key);
    };

    return {
        hintsHydrated: computed(() => hydrated.value),
        spotlightForRoute,
        hintForRoute,
        flowStepForRoute,
        markSeen,
        dismiss,
        advanceFlow,
        dismissFlow,
    };
}
